import React, { useState, useCallback } from 'react';

const InternalNotes = ({ notes = [], onAddNote, isSubmitting = false }) => {
  const [newNote, setNewNote] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  
  // Utiliser useCallback pour éviter des rendus inutiles
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    
    // On n'envoie que le contenu texte, le timestamp sera ajouté côté serveur
    onAddNote(newNote);
    setNewNote('');
    // Ne pas modifier showNotes ici pour éviter un rendu supplémentaire
  }, [newNote, onAddNote]);
  
  // Utiliser useCallback pour la fonction de toggle
  const toggleShowNotes = useCallback(() => {
    setShowNotes(prev => !prev);
  }, []);
  
  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    try {
      // Ajout d'une gestion d'erreur pour les formats de timestamp
      let dateObj;
      
      if (typeof date === 'object' && date.seconds) {
        // Format Firestore timestamp
        dateObj = new Date(date.seconds * 1000);
      } else if (typeof date === 'string') {
        // Format chaîne de caractères
        dateObj = new Date(date);
      } else if (date instanceof Date) {
        // Déjà un objet Date
        dateObj = date;
      } else {
        return 'Format de date inconnu';
      }
      
      // Vérifier si la date est valide
      if (isNaN(dateObj.getTime())) {
        return 'Date invalide';
      }
      
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(dateObj);
    } catch (error) {
      console.error('Erreur lors du formatage de la date:', error);
      return 'Erreur de date';
    }
  };
  
  // Éviter le remontage du composant en memoisant l'interface utilisateur
  return (
    <div className="mt-6 border-t border-base-200 pt-4">
      <div className="flex justify-between items-center mb-2">
        <h3 className="font-medium text-base-content">
          Notes internes ({notes.length})
          <span className="text-xs text-base-content/60 ml-2">(Visibles uniquement par les administrateurs)</span>
        </h3>
        <button 
          className="btn btn-sm btn-ghost"
          onClick={toggleShowNotes}
          type="button"
        >
          {showNotes ? 'Masquer' : 'Afficher'}
        </button>
      </div>
      
      {showNotes && (
        <>
          {notes.length > 0 ? (
            <div className="space-y-3 mb-4">
              {notes.map((note, index) => (
                <div key={`note-${index}-${note.timestamp?.seconds || Date.now()}`} className="bg-base-200/30 p-3 rounded-md">
                  <div className="flex justify-between mb-1">
                    <span className="font-medium text-sm text-base-content">{note.adminName}</span>
                    <span className="text-xs text-base-content/60">{formatDate(note.timestamp)}</span>
                  </div>
                  <p className="whitespace-pre-wrap text-sm text-base-content">{note.content}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center py-2 text-base-content/60 text-sm">Aucune note interne.</p>
          )}
          
          <form onSubmit={handleSubmit} className="mt-3">
            <textarea
              className="textarea textarea-bordered w-full h-20 text-sm bg-base-100 text-base-content"
              placeholder="Ajouter une note interne (visible uniquement par les administrateurs)..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              disabled={isSubmitting}
              required
            />
            <div className="flex justify-end mt-2">
              <button
                type="submit"
                className="btn btn-sm btn-primary"
                disabled={isSubmitting || !newNote.trim()}
              >
                {isSubmitting ? (
                  <>
                    <span className="loading loading-spinner loading-xs mr-1"></span>
                    Envoi...
                  </>
                ) : (
                  'Ajouter la note'
                )}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

// Export avec React.memo pour éviter des rendus inutiles
export default React.memo(InternalNotes);